<template>
  <div>
    <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
      <p class="ck-subtitulo mb-0" style="max-width: 760px">
        Quem acessa o painel e o que cada um pode fazer. O <b>administrador</b> acessa tudo. Os demais veem no menu só as telas
        liberadas, e o servidor também bloqueia o que não foi permitido.
      </p>
      <button class="btn btn-info" @click="editar()"><i class="bi bi-person-plus"></i> Novo usuário</button>
    </div>

    <div v-if="!catalogo.migrado && carregado" class="alert alert-warning small">
      Esta base ainda não tem as colunas de permissão. Todos os usuários continuam com acesso total até rodar a migração
      (<code>sql/migracao_feriados_justificativas_permissoes.sql</code> ou <code>controller/migrar.php</code>).
    </div>

    <div class="card mb-3">
      <div class="card-body py-2">
        <div class="input-group">
          <span class="input-group-text bg-white border-0"><i class="bi bi-search"></i></span>
          <input v-model.trim="busca" class="form-control border-0" placeholder="Pesquisar por nome ou login">
        </div>
      </div>
    </div>

    <div class="card">
      <div v-if="carregando" class="text-center py-5"><span class="spinner-border"></span></div>
      <div v-else class="table-responsive">
        <table class="table table-hover align-middle mb-0 lista">
          <thead><tr><th>Usuário</th><th>Acesso</th><th>Filial padrão</th><th>Último acesso</th><th style="width: 60px"></th></tr></thead>
          <tbody>
            <tr v-if="!filtrados.length"><td colspan="5" class="text-center text-muted py-4">Nenhum usuário encontrado.</td></tr>
            <tr v-for="u in filtrados" :key="u.id" :class="{ inativo: !u.ativo }" @click="editar(u)">
              <td>
                <div class="d-flex align-items-center gap-2">
                  <span class="avatar">{{ iniciais(u.nome) }}</span>
                  <div>
                    <div class="fw-semibold">{{ u.nome }} <span v-if="u.eu" class="badge badge-suave">você</span>
                      <span v-if="!u.ativo" class="badge text-bg-secondary">inativo</span></div>
                    <small class="text-muted">{{ u.login }}</small>
                  </div>
                </div>
              </td>
              <td>
                <span v-if="u.admin" class="badge text-bg-success"><i class="bi bi-shield-check"></i> Administrador</span>
                <span v-else class="small text-muted" :title="u.permissoes.map(nomePerm).join(', ')">
                  {{ u.permissoes.length }} permissão(ões): {{ u.permissoes.slice(0, 3).map(nomeCurto).join(', ') }}{{ u.permissoes.length > 3 ? '…' : '' }}
                </span>
              </td>
              <td class="small">{{ u.nomefilial || '—' }}</td>
              <td class="small text-muted">{{ u.ultimo_acesso ? dataHora(u.ultimo_acesso) : 'nunca' }}</td>
              <td class="text-end"><i class="bi bi-chevron-right text-muted"></i></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal -->
    <div class="modal fade" ref="modal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ form.id ? 'Editar usuário' : 'Novo usuário' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
          </div>
          <div class="modal-body">
            <div class="row g-4">
              <div class="col-lg-5">
                <div class="row g-2">
                  <div class="col-12"><label>Nome</label><input v-model.trim="form.nome" class="form-control" ref="nome" :disabled="bloqueado"></div>
                  <div class="col-6"><label>Login</label><input v-model.trim="form.login" class="form-control" autocomplete="off" :disabled="bloqueado"></div>
                  <div class="col-6">
                    <label>CPF</label>
                    <MaskInput type="text" mask="###.###.###-##" className="form-control" v-model="form.cpf" :disabled="bloqueado" :key="'cpf' + chave" />
                  </div>
                  <div class="col-12">
                    <label>{{ form.id ? 'Nova senha (opcional)' : 'Senha' }}</label>
                    <input type="password" v-model="form.senha" class="form-control" autocomplete="new-password" :disabled="bloqueado"
                           :placeholder="form.id ? 'deixe em branco para manter' : 'mínimo 6 caracteres'">
                  </div>
                  <div class="col-12">
                    <label>Filial padrão</label>
                    <select v-model.number="form.idempresa" class="form-select" :disabled="bloqueado">
                      <option :value="0" disabled>Selecione</option>
                      <option v-for="f in filiais" :key="f.id" :value="f.id">{{ f.text }}</option>
                    </select>
                  </div>
                  <div class="col-12 mt-2">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" id="usuativo" v-model="form.ativo" :disabled="bloqueado || form.eu">
                      <label class="form-check-label m-0" for="usuativo">Usuário ativo</label>
                    </div>
                    <small class="text-muted">Inativo não entra no sistema. As sessões abertas dele são encerradas.</small>
                  </div>
                  <div class="col-12">
                    <small class="text-muted"><i class="bi bi-info-circle"></i> O CPF identifica quem fez cada alteração gravada no REP (AFD).</small>
                  </div>
                </div>
              </div>

              <div class="col-lg-7">
                <div class="permissoes" :class="{ desligado: !catalogo.migrado }">
                  <div class="admin-box" :class="{ ligado: form.admin }">
                    <div class="form-check form-switch m-0">
                      <input class="form-check-input" type="checkbox" id="usuadmin" v-model="form.admin"
                             :disabled="!catalogo.eu_admin || bloqueado || (form.eu && form.admin)">
                      <label class="form-check-label m-0 fw-semibold" for="usuadmin"><i class="bi bi-shield-check"></i> Administrador (acesso total)</label>
                    </div>
                    <small class="text-muted d-block">
                      {{ catalogo.eu_admin ? 'Acessa todas as telas, inclusive usuários e permissões.' : 'Só um administrador pode conceder este acesso.' }}
                    </small>
                  </div>

                  <template v-if="!form.admin">
                    <div class="d-flex flex-wrap gap-2 align-items-center my-3">
                      <span class="small text-muted">Perfis prontos:</span>
                      <button v-for="p in PERFIS" :key="p.nome" type="button" class="btn btn-sm btn-light" :disabled="bloqueado" @click="aplicarPerfil(p)">{{ p.nome }}</button>
                      <button type="button" class="btn btn-sm btn-light text-danger" :disabled="bloqueado" @click="form.permissoes = []">Limpar</button>
                    </div>
                    <div v-for="(itens, grupo) in porGrupo" :key="grupo" class="grupo">
                      <div class="grupo-titulo">
                        <span>{{ grupo }}</span>
                        <button type="button" class="btn btn-link btn-sm p-0" :disabled="bloqueado" @click="alternarGrupo(itens)">
                          {{ itens.every(i => form.permissoes.includes(i.id)) ? 'desmarcar todas' : 'marcar todas' }}
                        </button>
                      </div>
                      <label v-for="p in itens" :key="p.id" class="perm" :class="{ marcado: form.permissoes.includes(p.id), indisponivel: !podeConceder(p.id) }"
                             :title="podeConceder(p.id) ? '' : 'Você não tem esta permissão, então não pode concedê-la.'">
                        <input type="checkbox" class="form-check-input" :value="p.id" v-model="form.permissoes" :disabled="bloqueado || !podeConceder(p.id)">
                        <span>{{ p.nome }}</span>
                      </label>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button v-if="form.id && !form.eu && !bloqueado" class="btn btn-light text-danger me-auto" @click="excluir"><i class="bi bi-trash"></i> Excluir</button>
            <span v-if="bloqueado" class="small text-muted me-auto"><i class="bi bi-lock"></i> Só um administrador altera outro administrador.</span>
            <button class="btn btn-light" data-bs-dismiss="modal">Fechar</button>
            <button v-if="!bloqueado" class="btn btn-info" :disabled="salvando" @click="salvar">Salvar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
//Eu sou o caminho, a verdade e a vida. Ninguém vem ao Pai senão por mim. (João 14:6);
import Swal from 'sweetalert2'
import axios from 'axios'
import { Modal } from 'bootstrap'
import util from '@/util'
import MaskInput from '@/components/MaskInput.vue'

const PERFIS = [
  { nome: 'Gestor de ponto', perms: ['painel', 'registros', 'espelho', 'escalas', 'justificativas', 'justificativas_aprovar', 'fiscal', 'feriados'] },
  { nome: 'Lançar justificativas', perms: ['justificativas', 'espelho'] },
  { nome: 'Cadastros', perms: ['colaboradores', 'jornadas', 'feriados', 'zonas', 'cadastros'] },
  { nome: 'Consulta', perms: ['painel', 'espelho'] },
]

export default {
  name: 'Usuarios',
  components: { MaskInput },
  data() {
    return {
      PERFIS,
      catalogo: { permissoes: [], migrado: true, eu_admin: true, minhas: [] },
      lista: [],
      filiais: [],
      busca: '',
      carregando: false,
      carregado: false,
      salvando: false,
      modal: null,
      chave: 0,
      form: this.novoForm(),
    }
  },
  computed: {
    filtrados() {
      const b = this.busca.toLowerCase()
      return b ? this.lista.filter(u => u.nome.toLowerCase().includes(b) || u.login.toLowerCase().includes(b)) : this.lista
    },
    porGrupo() {
      const g = {}
      this.catalogo.permissoes.forEach(p => { (g[p.grupo] = g[p.grupo] || []).push(p) })
      return g
    },
    /** Usuário admin sendo visto por quem não é admin: só leitura. */
    bloqueado() { return !!this.form.id && this.form.adminOriginal && !this.catalogo.eu_admin },
  },
  mounted() {
    this.carregar()
    this.carregarFiliais()
  },
  beforeUnmount() {
    if (this.modal) this.modal.dispose()
  },
  methods: {
    novoForm() {
      return { id: 0, nome: '', login: '', cpf: '', senha: '', idempresa: 0, ativo: true, admin: false, adminOriginal: false, eu: false, permissoes: [] }
    },
    iniciais(n) { return String(n || '?').trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase() },
    dataHora(v) { return util.dataBR(v) + ' ' + String(v).substring(11, 16) },
    nomePerm(id) { const p = this.catalogo.permissoes.find(x => x.id === id); return p ? p.nome : id },
    nomeCurto(id) { return this.nomePerm(id).split(' (')[0] },
    podeConceder(id) { return this.catalogo.eu_admin || this.catalogo.minhas.includes(id) },
    req(op, extra = []) {
      return util.reqaxios([{ parametro: 'op', valor: op }, { parametro: 'idempresa', valor: this.$store.state.usuario.idempresa }, ...extra],
                           'post', util.servidor + '/controller/usuarios.php')
    },
    async carregar() {
      this.carregando = true
      try {
        const [cat, r] = await Promise.all([this.req('permissoes'), this.req('listagem', [{ parametro: 'limit', valor: 1000 }, { parametro: 'offset', valor: 0 }])])
        if (cat.data && cat.data.permissoes) this.catalogo = cat.data
        this.lista = Array.isArray(r.data) ? r.data : []
        if (!Array.isArray(r.data)) util.respostaOk(r.data)
        this.carregado = true
      } finally {
        this.carregando = false
      }
    },
    async carregarFiliais() {
      const r = await axios.get(util.servidor + '/controller/select2sql.php', {
        params: { idempresa: this.$store.state.usuario.idempresa, token: util.token(), tabela: 'tbfiliais' },
      }).catch(() => ({ data: [] }))
      this.filiais = Array.isArray(r.data) ? r.data : []
    },
    editar(u) {
      this.form = u
        ? { id: u.id, nome: u.nome, login: u.login, cpf: u.cpf || '', senha: '', idempresa: parseInt(u.idfilialpadrao) || 0,
            ativo: !!u.ativo, admin: !!u.admin, adminOriginal: !!u.admin, eu: u.eu, permissoes: [...u.permissoes] }
        : { ...this.novoForm(), idempresa: this.filiais.length === 1 ? this.filiais[0].id : 0 }
      this.chave++ // recria o campo de CPF com máscara
      if (!this.modal) this.modal = new Modal(this.$refs.modal)
      this.modal.show()
      setTimeout(() => this.$refs.nome && this.$refs.nome.focus(), 300)
    },
    aplicarPerfil(p) {
      this.form.permissoes = p.perms.filter(id => this.podeConceder(id))
    },
    alternarGrupo(itens) {
      const ids = itens.map(i => i.id).filter(id => this.podeConceder(id))
      const todos = ids.every(id => this.form.permissoes.includes(id))
      this.form.permissoes = todos ? this.form.permissoes.filter(id => !ids.includes(id)) : [...new Set([...this.form.permissoes, ...ids])]
    },
    async salvar() {
      const f = this.form
      if (!f.nome || !f.login) return util.notif('error', 'Atenção', 'Informe nome e login.')
      if (String(f.cpf).replace(/\D/g, '').length !== 11) return util.notif('error', 'Atenção', 'Informe o CPF.')
      if (!f.id && f.senha.length < 6) return util.notif('error', 'Atenção', 'A senha deve ter pelo menos 6 caracteres.')
      if (!f.idempresa) return util.notif('error', 'Atenção', 'Selecione a filial padrão.')
      if (this.catalogo.migrado && !f.admin && !f.permissoes.length) return util.notif('error', 'Atenção', 'Marque ao menos uma permissão.')
      this.salvando = true
      try {
        const reg = { id: f.id, nome: f.nome, login: f.login, cpf: String(f.cpf).replace(/\D/g, ''), senha: f.senha,
                      idempresa: f.idempresa, ativo: f.ativo ? 1 : 0, admin: f.admin ? 1 : 0, permissoes: f.permissoes }
        const r = await this.req('rotinacrud', [{ parametro: 'opcrud', valor: f.id ? 2 : 1 }, { parametro: 'registro', valor: JSON.stringify(reg) }])
        if (util.respostaOk(r.data)) {
          this.modal.hide()
          util.notif('success', 'Usuário salvo', f.eu ? 'Suas permissões valem a partir do próximo acesso às telas.' : '')
          this.carregar()
        }
      } finally {
        this.salvando = false
      }
    },
    async excluir() {
      const ok = await Swal.fire({ title: `Excluir ${this.form.nome}?`, text: 'Para manter o histórico, prefira desmarcar "Usuário ativo".',
                                   icon: 'warning', showCancelButton: true, confirmButtonText: 'Excluir', cancelButtonText: 'Voltar' })
      if (!ok.isConfirmed) return
      const r = await this.req('rotinacrud', [{ parametro: 'opcrud', valor: 3 }, { parametro: 'registro', valor: JSON.stringify({ id: this.form.id }) }])
      if (util.respostaOk(r.data)) { this.modal.hide(); this.carregar() }
    },
  },
}
</script>

<style scoped lang="scss">
.lista th { font-size: .72rem; text-transform: uppercase; color: var(--ck-texto-2); background: #f7faf9; letter-spacing: .04em; }
.lista tbody tr { cursor: pointer; }
.lista tr.inativo td { color: #93a29d; }
.avatar {
  width: 34px; height: 34px; border-radius: 50%; background: var(--ck-primaria-suave); color: var(--ck-primaria);
  display: inline-flex; align-items: center; justify-content: center; font-weight: 700; font-size: .8rem; flex-shrink: 0;
}
.permissoes.desligado { opacity: .55; pointer-events: none; }
.admin-box { border: 1px solid var(--ck-borda); border-radius: 12px; padding: 10px 14px; background: #f7faf9; }
.admin-box.ligado { border-color: var(--ck-sucesso); background: #eef8f2; }
.grupo { margin-bottom: 12px; }
.grupo-titulo { display: flex; justify-content: space-between; align-items: center; font-size: .72rem; text-transform: uppercase;
  letter-spacing: .05em; font-weight: 700; color: var(--ck-primaria); margin-bottom: 4px; }
.grupo-titulo .btn-link { font-size: .72rem; text-transform: none; letter-spacing: 0; }
.perm {
  display: flex; align-items: center; gap: 10px; padding: 8px 10px; border: 1px solid var(--ck-borda); border-radius: 10px;
  margin: 0 0 6px; cursor: pointer; font-size: .86rem; font-weight: 500; color: var(--ck-texto); width: 100%;
}
.perm .form-check-input { margin: 0; }
.perm.marcado { border-color: var(--ck-primaria-2); background: var(--ck-primaria-suave); }
.perm.indisponivel { opacity: .5; cursor: not-allowed; }
</style>
